import { useState, useEffect, useCallback, useMemo } from 'react';
import { useStore } from '../../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  listRoles,
  listUserAssignments,
  bulkAssignAgencyRolesToUser,
  bulkAssignClinicRolesToUser,
} from '../../../services/api';
import type {
  RoleResponse,
  RoleAssignmentResponse,
} from '../../../services/api';
import type { OrgMemberResponse } from '../../clinician-manager/types';
import {
  collectAssignableClinicIds,
  filterUserVisibleRoles,
  HIDDEN_CLINIC_ROLE_SLUGS,
} from '../../../utils/rbacRoles';
import {
  buildInitialClinicSelectionFromAssignments,
  clinicRoleAssignmentsForSave,
  hiddenClinicIdsForPicker,
  type ClinicSelectionMap,
} from './clinicRoleSelection';

// ─── Types ───────────────────────────────────────────────────────────────────

/** role_id → whether it's selected (agency scope) */
type AgencySelectionMap = Map<string, boolean>;

// ─── Hook ────────────────────────────────────────────────────────────────────

export type RoleAssignmentDialogState = ReturnType<
  typeof useRoleAssignmentDialog
>;

export class RoleAssignmentSaveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RoleAssignmentSaveError';
  }
}

type RoleAssignmentDialogOptions = {
  /**
   * Clinic IDs hidden from the role picker. Existing clinic-scoped assignments
   * at these clinics are re-sent on save so they are not reconciled away.
   */
  preserveRoleAssignmentsForClinicIds?: ReadonlySet<string>;
  /**
   * Clinics the actor can administer (`GET /agency-clinics/`). Assignments
   * outside this set are hidden from the picker and preserved on save
   * (EMR-783), same as Kinnser-hidden ids.
   */
  visibleClinicIds?: ReadonlySet<string>;
};

export function useRoleAssignmentDialog(
  member: OrgMemberResponse | null,
  onSuccess: () => void,
  onBeforeSave?: () => Promise<void>,
  onAfterSave?: () => Promise<void>,
  options?: RoleAssignmentDialogOptions
) {
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const [agencyRoles, setAgencyRoles] = useState<RoleResponse[]>([]);
  const [clinicRoles, setClinicRoles] = useState<RoleResponse[]>([]);
  /**
   * Clinic roles hidden from the grid. Kept separately because the grid's list
   * has them filtered out, and the save path needs their ids to send existing
   * assignments back: the writer reconciles, so anything omitted is deleted.
   */
  const [hiddenClinicRoleIds, setHiddenClinicRoleIds] = useState<Set<string>>(
    new Set()
  );
  const [currentAssignments, setCurrentAssignments] = useState<
    RoleAssignmentResponse[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /** role_id → selected for agency-scoped roles */
  const [agencySelection, setAgencySelection] = useState<AgencySelectionMap>(
    new Map()
  );

  /** role_id → Set<clinic_id> for clinic-scoped roles */
  const [clinicSelection, setClinicSelection] = useState<ClinicSelectionMap>(
    new Map()
  );

  /**
   * Clinics revoked in the access list during this dialog session. Nothing is
   * preserved at them on save, so a grid-hidden role cannot outlive the revoke.
   */
  const [revokedClinicIds, setRevokedClinicIds] = useState<Set<string>>(
    new Set()
  );

  // ── Load roles + current assignments ──

  useEffect(() => {
    if (!member?.roger_user_id) return;

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const [rolesRes, assignmentsRes] = await Promise.all([
          listRoles({
            query: { limit: 500 },
            throwOnError: true,
          }),
          listUserAssignments({
            path: { user_id: member.roger_user_id! },
            throwOnError: true,
          }),
        ]);

        if (cancelled) return;

        const roles: RoleResponse[] = rolesRes.data ?? [];
        const assignments: RoleAssignmentResponse[] = assignmentsRes.data ?? [];

        setAgencyRoles(
          filterUserVisibleRoles(roles.filter(r => r.scope === 'agency'))
        );
        const allClinicRoles = filterUserVisibleRoles(
          roles.filter(r => r.scope === 'clinic')
        );
        setClinicRoles(
          allClinicRoles.filter(r => !HIDDEN_CLINIC_ROLE_SLUGS.includes(r.slug))
        );
        setHiddenClinicRoleIds(
          new Set(
            allClinicRoles
              .filter(r => HIDDEN_CLINIC_ROLE_SLUGS.includes(r.slug))
              .map(r => r.id)
          )
        );
        setCurrentAssignments(assignments);

        // Build initial selection from current assignments
        const newAgencySelection: AgencySelectionMap = new Map();

        for (const a of assignments) {
          if (a.clinic_id == null) {
            // Agency-scoped assignment
            newAgencySelection.set(a.role_id, true);
          }
        }

        setAgencySelection(newAgencySelection);
        setRevokedClinicIds(new Set());
        setClinicSelection(
          buildInitialClinicSelectionFromAssignments(
            assignments,
            hiddenClinicIdsForPicker({
              assignments,
              visibleClinicIds: options?.visibleClinicIds,
              preserveClinicIds: options?.preserveRoleAssignmentsForClinicIds,
            })
          )
        );
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load role assignment data:', err);
          showError('Unable to load role data. Please try again.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    member?.roger_user_id,
    options?.preserveRoleAssignmentsForClinicIds,
    options?.visibleClinicIds,
    showError,
  ]);

  // ── Agency role toggles ──

  const toggleAgencyRole = useCallback((roleId: string) => {
    setAgencySelection(prev => {
      const next = new Map(prev);
      next.set(roleId, !prev.get(roleId));
      return next;
    });
  }, []);

  // ── Clinic role toggles ──

  const toggleClinicForRole = useCallback(
    (roleId: string, clinicId: string) => {
      setClinicSelection(prev => {
        const next = new Map(prev);
        const clinics = new Set(prev.get(roleId) ?? []);
        if (clinics.has(clinicId)) {
          clinics.delete(clinicId);
        } else {
          clinics.add(clinicId);
        }
        next.set(roleId, clinics);
        return next;
      });
    },
    []
  );

  /**
   * Drop one clinic from every clinic-scoped role.
   *
   * Revoking clinic access is the caller: a role at a clinic the member cannot
   * see cannot be acted on, and leaving the pair behind would re-grant the
   * access on save, because the role save creates the user_clinic entry.
   *
   * The clinic is remembered as revoked because clearing the picker is not
   * enough on its own: the save re-sends grid-hidden roles (approver) from the
   * assignments it loaded, which would put the pair back. Remembering it is
   * also one-directional, matching the roles: re-checking access grants the
   * clinic without restoring the role that used to grant it.
   */
  const removeClinicFromAllRoles = useCallback((clinicId: string) => {
    setRevokedClinicIds(prev => {
      if (prev.has(clinicId)) return prev;
      const next = new Set(prev);
      next.add(clinicId);
      return next;
    });
    setClinicSelection(prev => {
      let changed = false;
      const next = new Map(prev);
      for (const [roleId, clinicIds] of prev) {
        if (!clinicIds.has(clinicId)) continue;
        const remaining = new Set(clinicIds);
        remaining.delete(clinicId);
        next.set(roleId, remaining);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, []);

  /**
   * Forget that a clinic was revoked, so a grid-hidden role there is preserved
   * again. Re-checking the access box is the caller. The visible role
   * checkboxes stay cleared, because that cascade is deliberately
   * one-directional and the operator can see what it did, but the approver is
   * invisible: dropping it because a box was unticked and immediately
   * reticked would take somebody out of scheduling with nothing on screen
   * saying so.
   */
  const clearClinicRevocation = useCallback((clinicId: string) => {
    setRevokedClinicIds(prev => {
      if (!prev.has(clinicId)) return prev;
      const next = new Set(prev);
      next.delete(clinicId);
      return next;
    });
  }, []);

  const selectAllClinicsForRole = useCallback(
    (roleId: string, allClinicIds: string[]) => {
      setClinicSelection(prev => {
        const next = new Map(prev);
        next.set(roleId, new Set(allClinicIds));
        return next;
      });
    },
    []
  );

  const clearClinicsForRole = useCallback((roleId: string) => {
    setClinicSelection(prev => {
      const next = new Map(prev);
      next.set(roleId, new Set());
      return next;
    });
  }, []);

  const assignableClinicIds = useMemo((): Set<string> | undefined => {
    if (isLoading) return undefined;
    const ids = collectAssignableClinicIds(clinicRoles, clinicSelection);
    if (!ids) return undefined;

    const hiddenClinicIds = hiddenClinicIdsForPicker({
      assignments: currentAssignments,
      visibleClinicIds: options?.visibleClinicIds,
      preserveClinicIds: options?.preserveRoleAssignmentsForClinicIds,
    });
    if (!hiddenClinicIds.size) return ids;

    return new Set([...ids].filter(clinicId => !hiddenClinicIds.has(clinicId)));
  }, [
    isLoading,
    clinicRoles,
    clinicSelection,
    currentAssignments,
    options?.visibleClinicIds,
    options?.preserveRoleAssignmentsForClinicIds,
  ]);

  // ── Save ──

  const handleSave = useCallback(async () => {
    if (!member?.roger_user_id) return;

    setIsSaving(true);
    try {
      if (onBeforeSave) {
        await onBeforeSave();
      }

      const selectedAgencyRoleIds = [...agencySelection.entries()]
        .filter(([, selected]) => selected)
        .map(([roleId]) => roleId);

      // Approver (and other grid-hidden clinic roles) still have to be sent
      // back. The writer reconciles, so an assignment left out of the payload
      // is one it soft-deletes -- and approver is still what makes a handful
      // of people visible to scheduling at all. Clinics the actor cannot
      // administer are omitted here; the backend preserves them (EMR-783).
      const clinicAssignments = clinicRoleAssignmentsForSave({
        clinicSelection,
        currentAssignments,
        hiddenClinicIds: hiddenClinicIdsForPicker({
          assignments: currentAssignments,
          visibleClinicIds: options?.visibleClinicIds,
          preserveClinicIds: options?.preserveRoleAssignmentsForClinicIds,
        }),
        preserveClinicIds: options?.preserveRoleAssignmentsForClinicIds,
        preserveRoleIds: hiddenClinicRoleIds,
        revokedClinicIds,
      });

      await Promise.all([
        bulkAssignAgencyRolesToUser({
          path: { user_id: member.roger_user_id },
          body: { role_ids: selectedAgencyRoleIds },
          throwOnError: true,
        }),
        bulkAssignClinicRolesToUser({
          path: { user_id: member.roger_user_id },
          body: { assignments: clinicAssignments },
          throwOnError: true,
        }),
      ]);

      if (onAfterSave) {
        await onAfterSave();
      }

      showSuccess('Role assignments updated successfully.');
      onSuccess();
    } catch (err) {
      console.error('Failed to save role assignments:', err);
      if (!(err instanceof RoleAssignmentSaveError)) {
        showError('Failed to save role assignments. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    member,
    agencySelection,
    clinicSelection,
    currentAssignments,
    hiddenClinicRoleIds,
    revokedClinicIds,
    options?.preserveRoleAssignmentsForClinicIds,
    options?.visibleClinicIds,
    showSuccess,
    showError,
    onSuccess,
    onBeforeSave,
    onAfterSave,
  ]);

  return {
    agencyRoles,
    clinicRoles,
    currentAssignments,
    isLoading,
    isSaving,
    agencySelection,
    clinicSelection,
    assignableClinicIds,
    toggleAgencyRole,
    toggleClinicForRole,
    removeClinicFromAllRoles,
    clearClinicRevocation,
    selectAllClinicsForRole,
    clearClinicsForRole,
    handleSave,
  };
}
